import React, { useState } from 'react';
import { Form, Modal, Typography, Upload } from 'antd';
import type { UploadFile } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { CrawlerImportPayload } from '../../../domain/entities/Crawler';

interface ImportCrawlerModalProps {
  open: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onSubmit: (payload: CrawlerImportPayload) => Promise<void>;
}

const ImportCrawlerModal: React.FC<ImportCrawlerModalProps> = ({ open, isSaving, onCancel, onSubmit }) => {
  const { t } = useTranslation();
  const [profileFile, setProfileFile] = useState<File | undefined>();
  const [sessionFile, setSessionFile] = useState<File | undefined>();

  const reset = () => {
    setProfileFile(undefined);
    setSessionFile(undefined);
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const handleSubmit = async () => {
    if (!profileFile || !sessionFile) return;
    await onSubmit({ profileFile, sessionFile });
    reset();
  };

  const toFileList = (file?: File): UploadFile[] => (file ? [{ uid: file.name, name: file.name, status: 'done' }] : []);

  return (
    <Modal
      open={open}
      title={t('crawler-import', { ns: 'titles' })}
      okText={t('crawler-import', { ns: 'buttons' })}
      cancelText={t('cancel', { ns: 'buttons' })}
      confirmLoading={isSaving}
      okButtonProps={{ disabled: !profileFile || !sessionFile }}
      onCancel={handleCancel}
      onOk={handleSubmit}
      destroyOnClose
    >
      <Typography.Paragraph type="secondary">{t('crawler-import-hint', { ns: 'standard' })}</Typography.Paragraph>
      <Form layout="vertical" requiredMark={false}>
        <Form.Item label={t('crawler-import-profile', { ns: 'labels' })} extra={t('crawler-import-profile-hint', { ns: 'standard' })}>
          <Upload.Dragger
            accept=".json"
            multiple={false}
            fileList={toFileList(profileFile)}
            beforeUpload={(file) => {
              setProfileFile(file);
              return false;
            }}
            onRemove={() => setProfileFile(undefined)}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">{t('crawler-import-drop-file', { ns: 'standard' })}</p>
          </Upload.Dragger>
        </Form.Item>
        <Form.Item label={t('crawler-import-session', { ns: 'labels' })} extra={t('crawler-import-session-hint', { ns: 'standard' })}>
          <Upload.Dragger
            accept=".session"
            multiple={false}
            fileList={toFileList(sessionFile)}
            beforeUpload={(file) => {
              setSessionFile(file);
              return false;
            }}
            onRemove={() => setSessionFile(undefined)}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">{t('crawler-import-drop-file', { ns: 'standard' })}</p>
          </Upload.Dragger>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ImportCrawlerModal;
